import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { BACKEND_URL } from '../utils/backendURL';
import { CheckCircle, X, RefreshCw, Calendar, Users, Building, Phone, Mail, MessageSquare } from 'lucide-react';

const DemoRequestModal = ({ isOpen, onClose, portalStyle = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    team_size: '',
    phone: '',
    message: '',
    preferred_date: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const teamSizeOptions = [
    '1-5',
    '6-20',
    '21-50',
    '51-200',
    '201-500',
    '500+'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo-requests/`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        // Reset form after 3 seconds and close
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            team_size: '',
            phone: '',
            message: '',
            preferred_date: ''
          });
          setSubmitted(false);
          onClose();
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting demo request:', err);
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        company: '',
        team_size: '',
        phone: '',
        message: '',
        preferred_date: ''
      });
      setError('');
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`${portalStyle ? 'lexa-portal lexa-portal-content' : ''} max-w-2xl max-h-[90vh] overflow-y-auto`}>
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                Request a Demo
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Fill out the form below and our team will contact you within 24 hours to schedule a personalized demo.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Name - Required */}
              <div>
                <Label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4" />
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className="w-full"
                />
              </div>

              {/* Email - Required */}
              <div>
                <Label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4" />
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@company.com"
                  className="w-full"
                />
              </div>

              {/* Company */}
              <div>
                <Label htmlFor="company" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4" />
                  Company Name
                </Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Acme Inc."
                  className="w-full"
                />
              </div>

              {/* Team Size */}
              <div>
                <Label htmlFor="team_size" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4" />
                  Team Size
                </Label>
                <select
                  id="team_size"
                  name="team_size"
                  value={formData.team_size}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select team size</option>
                  {teamSizeOptions.map(option => (
                    <option key={option} value={option}>{option} people</option>
                  ))}
                </select>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full"
                />
              </div>

              {/* Preferred Date */}
              <div>
                <Label htmlFor="preferred_date" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4" />
                  Preferred Date/Time
                </Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  type="text"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  placeholder="e.g., Next Monday at 2pm EST"
                  className="w-full"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="h-4 w-4" />
                  Message (Optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your testing needs..."
                  rows={4}
                  className="w-full"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <X className="h-4 w-4" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Request Demo
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Request Submitted Successfully!
            </h3>
            <p className="text-gray-600 mb-4">
              Thank you for your interest! Our team will contact you within 24 hours to schedule your personalized demo.
            </p>
            <p className="text-sm text-gray-500">
              Check your email for a confirmation message.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestModal;
